import { Typography } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion, useReducedMotion } from "framer-motion";

import { HERO } from "./constants";
import { AuthShowcaseCard } from "./showcase-card";

import { CONSTANTS } from "@/config/constants";

// Everything here is themed to the image, not to the app: the wordmark comes
// from CONSTANTS.LOGO_FULL rather than useLogo(), which swaps to the dark-ink
// version under a light theme and would vanish against this background.
type AuthHeroPanelProps = {
  headline?: string;
};

export const AuthHeroPanel = ({
  headline = HERO.headline,
}: AuthHeroPanelProps) => {
  const reduceMotion = useReducedMotion();

  const rise = (delay: number) => ({
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: reduceMotion ? 0 : 0.5,
      delay: reduceMotion ? 0 : delay,
    },
  });

  return (
    <div className="relative hidden overflow-hidden lg:sticky lg:top-0 lg:block lg:h-dvh">
      <img
        alt=""
        className="absolute inset-0 size-full object-cover"
        src="/hero-bg.webp"
      />

      {/* The image runs dark top-left to white bottom-right and all the copy
      sits low, so the lower two thirds need darkening to stay legible. */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/35 to-transparent"
      />

      <div className="relative flex h-full flex-col justify-between gap-10 p-10 xl:p-12">
        <motion.img
          {...rise(0)}
          alt={CONSTANTS.BRAND_NAME}
          className="h-8 w-auto self-start object-contain"
          src={CONSTANTS.LOGO_FULL}
        />

        <div className="flex flex-col gap-8">
          <motion.div {...rise(0.1)} className="flex justify-start">
            <AuthShowcaseCard />
          </motion.div>

          <motion.div {...rise(0.2)}>
            {/* lg, not md: at md the two brands' headlines wrap differently. */}
            <Typography className="max-w-lg text-white" type="h2">
              {headline}
            </Typography>
          </motion.div>

          <motion.div {...rise(0.3)} className="flex flex-wrap gap-2">
            {HERO.features.map((feature) => (
              <div
                key={feature.label}
                className="auth-media-panel flex items-center gap-2 rounded-full px-3 py-1.5 text-white/85"
              >
                <Icon className="flex-none" icon={feature.icon} width={16} />
                <Typography
                  className="text-current"
                  type="body-xs"
                  weight="medium"
                >
                  {feature.label}
                </Typography>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
};
